<template>
  <div id="careers-positions" class="careers">
    <div class="container">
      <h2 class="careers__title">Open roles at Infermedica</h2>
      <section
        v-for="department in Object.keys(departments)"
        :key="department"
        class="careers__department-section"
      >
        <h3 class="careers__department-name heading--4">
          {{ department }}
        </h3>
        <div class="careers__department-positions">
          <nuxt-link
            v-for="position in departments[department]"
            :key="position.id"
            class="careers__position"
            :to="`/careers/${position.id}`"
          >
            <div class="careers__position-info">
              <h4 class="careers__position-name heading--5">
                {{ position.title }}
              </h4>
              <p class="careers__position-location">
                {{ getLocation(position) }}
              </p>
              <p
                v-if="formatSalaryForDisplay(position)"
                class="careers__position-salary"
              >
                {{ formatSalaryForDisplay(position) }}
              </p>
            </div>
          </nuxt-link>
        </div>
      </section>
    </div>
  </div>
</template>

<script lang="ts" setup>
import type { JobDataSummary } from '@/types'
import { formatSalaryForDisplay } from '@/utils/careers'

interface Props {
  data: JobDataSummary[]
}

const props = defineProps<Props>()

const departments = computed(() => {
  const jobsByDepartment = props.data.reduce(
    (acc, curr) => {
      const departmentKey = curr.department?.trim() || 'Other'

      if (!acc[departmentKey]) {
        acc[departmentKey] = []
      }

      acc[departmentKey].push(curr)
      return acc
    },
    {} as Record<string, JobDataSummary[]>,
  )

  if (jobsByDepartment['Other']) {
    const otherJobs = jobsByDepartment['Other']
    delete jobsByDepartment['Other']
    jobsByDepartment['Other'] = otherJobs
  }

  return jobsByDepartment
})

const getLocation = (posting: JobDataSummary) => {
  return `Remote${posting.country_id ? ' / ' + posting.country_id : ''}`
}
</script>

<style lang="scss" scoped>
.careers {
  background-color: white;
  padding: 96px 0;

  &__title {
    margin-bottom: 60px;
  }

  &__department-section {
    border-top: 1px solid var(--gray-400);
    padding: 24px 0;
    display: grid;
    grid-template-columns: 1fr;
    column-gap: 46px;

    &:last-child {
      border-bottom: 1px solid var(--gray-400);
    }

    @media (min-width: $breakpoint-md) {
      padding: 40px 0;
      grid-template-columns: 210px 1fr;
    }
  }

  &__department-name {
    margin-bottom: 28px;
  }

  &__position {
    text-decoration: none;
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding-left: 24px;

    @media (min-width: $breakpoint-md) {
      padding-left: 0;
    }

    &::after {
      content: '';
      display: block;
      flex-shrink: 0;

      @include icon('@/assets/icons/16-chevron-right-navy.svg', 16px, 16px);
    }

    &:not(:last-child) {
      @media (min-width: $breakpoint-md) {
        border-bottom: 1px solid var(--gray-400);
        padding-bottom: 40px;
      }
    }

    &:not(:first-child) {
      padding-top: 28px;

      @media (min-width: $breakpoint-md) {
        padding-bottom: 28px;
      }
    }

    &:last-child {
      padding-bottom: 0;
    }

    &:hover {
      .careers__position-name {
        color: var(--color-link);
        transform: translateX(12px);
      }

      &::after {
        @include icon('@/assets/icons/16-chevron-right-cobalt.svg', 16px, 16px);
      }
    }
  }

  &__position-name {
    margin-bottom: 4px;
    transition: transform 0.3s;
  }

  &__position-location {
    margin-bottom: 4px;
    color: var(--cobalt-800);
    transition: transform 0.3s;
  }

  &__position-info {
    margin-right: 45px;
  }

  &__position-salary {
    margin-bottom: 0;
    color: var(--cobalt-800);
    transition: transform 0.3s;
  }
}
</style>
